import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import '../styles/Register.css';

const Register = ({ apiBaseUrl }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', buttons: [], icon: null });

  useEffect(() => {
    // Fetch invite details
    fetch(`${apiBaseUrl}/users/invite/${token}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.message === 'Invite expired or invalid') {
          setModal({
            isOpen: true,
            title: 'Error',
            message: 'This invite is expired or invalid.',
            buttons: [{ label: 'OK', class: 'btn btn-primary', action: () => navigate('/') }],
            icon: <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />,
          });
        } else {
          setInvite(data);
        }
      })
      .catch(err => {
        console.error('Error fetching invite:', err);
        setModal({
          isOpen: true,
          title: 'Error',
          message: 'Failed to load invite details.',
          buttons: [{ label: 'OK', class: 'btn btn-primary', action: () => navigate('/') }],
          icon: <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />,
        });
      });
  }, [apiBaseUrl, token, navigate]);

  const showModal = (title, message, buttons, icon = null) => {
    setModal({ isOpen: true, title, message, buttons, icon });
  };

  const closeModal = () => setModal({ ...modal, isOpen: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      showModal(
        'Error',
        'Passwords do not match.',
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      return;
    }

    const payload = invite.user_id
      ? { password, confirm_password: confirmPassword }
      : { username: username.toLowerCase().trim(), password, confirm_password: confirmPassword };

    try {
      const res = await fetch(`${apiBaseUrl}/users/invite/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.message === 'Account created' || data.message === 'Password reset successfully') {
        showModal(
          'Success',
          data.message === 'Account created' ? 'Account created successfully!' : 'Password reset successfully!',
          [{ label: 'OK', class: 'btn btn-primary', action: () => navigate('/login') }],
          <img src="/static/icons/check-circle-solid.svg" alt="Success" className="modal-icon" />
        );
      } else {
        showModal(
          'Error',
          data.message || 'Failed to process request.',
          [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
          <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
        );
      }
    } catch (err) {
      showModal(
        'Error',
        'An error occurred. Please try again.',
        [{ label: 'OK', class: 'btn btn-primary', action: () => {} }],
        <img src="/static/icons/xmark-circle-solid.svg" alt="Error" className="modal-icon" />
      );
      console.error('Error submitting form:', err);
    }
  };

  if (!invite) return null;

  return (
    <div className="register-background">
      <div className="register-panel">
        <h2>{invite.user_id ? 'Reset Password' : 'Register'}</h2>
        <form onSubmit={handleSubmit}>
          {!invite.user_id && (
            <>
              <label htmlFor="username">Username</label>
              <input
                name="username"
                className="input-field"
                required
                placeholder="Choose a username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </>
          )}
          <label htmlFor="password">Password</label>
          <input
            name="password"
            type="password"
            className="input-field"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <label htmlFor="confirm_password">Confirm Password</label>
          <input
            name="confirm_password"
            type="password"
            className="input-field"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button type="submit" className="btn btn-primary">
            <img src="/static/icons/right-to-bracket-solid.svg" className="icon" alt="Submit" />
            {invite.user_id ? 'Reset Password' : 'Create Account'}
          </button>
        </form>
      </div>
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        buttons={modal.buttons}
        icon={modal.icon}
      />
    </div>
  );
};

export default Register;